import React, { useState, useEffect, useMemo } from 'react';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { getDatabase, ref, set, onValue, update } from 'firebase/database';
import {
  FiUser,
  FiMail,
  FiPhone,
  FiLock,
  FiUserPlus,
  FiEdit3,
  FiTrash2,
  FiEye,
  FiEyeOff,
  FiShield,
  FiSettings,
  FiPlus,
  FiX,
  FiCheck,
  FiClock,
  FiChevronDown,
  FiChevronUp,
  FiUsers,
  FiSearch
} from 'react-icons/fi';

const MODULES = [
  'dashboard',
  'customers',
  'orders',
  'sales-dashboard',
  'employees',
  'total-trainings',
  'products',
  'offer-products',
  'users',
  'add-admin',
  'levels',
  'withdraw-requests',
  'document-verification',
  'settings',
  'register-training-users',
  'reports',
  'withdraw-report',
  'order-cancel-report',
  'training-users-list',
  'training-payment-report',
  'referral-tree',
  'inventory-report',
  'purchase-entry',
  'payment-entry',
  'training-entry',
  'creditor-ledger',
  'debtor-ledger',
  'balance-sheet',
  'fixed-asset-ledger',
  'loan-account-ledger',
  'gst-payable-ledger',
  'stock-balance'
];

const makeDefaultPermissions = () =>
  MODULES.reduce((acc, mod) => ({ ...acc, [mod]: { read: false, write: false, delete: false, add: false } }), {});

const AddAdminPage = () => {
  const auth = useMemo(() => getAuth(), []);
  const db = useMemo(() => getDatabase(), []);

  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', role: 'subadmin', permissions: makeDefaultPermissions() });

  const [isFormVisible, setIsFormVisible] = useState(false);
  const [expandedModule, setExpandedModule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [msgType, setMsgType] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [admins, setAdmins] = useState({});
  const [selected, setSelected] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [suspendMode, setSuspendMode] = useState(null);

  const [customSuspendValue, setCustomSuspendValue] = useState(1);
  const [customSuspendUnit, setCustomSuspendUnit] = useState('days');
  const [searchQuery, setSearchQuery] = useState('');

  const [selectedCard, setSelectedCard] = useState(null);

  useEffect(() => {
    const adminsRef = ref(db, 'HTAMS/users');
    const unsubscribe = onValue(adminsRef, (snapshot) => {
      const data = snapshot.val() || {};
      const onlyAdmins = Object.entries(data)
        .filter(([, user]) => user.role === 'admin' || user.role === 'subadmin')
        .reduce((acc, [uid, user]) => ({ ...acc, [uid]: user }), {});
      setAdmins(onlyAdmins);
    });
    return () => unsubscribe();
  }, [db]);

  const handleChange = (name, value, isEdit = false) => {
    let processedValue = value;

    if (name === 'name') {
      processedValue = value.replace(/\d/g, '');
    } else if (name === 'phone') {
      processedValue = value.replace(/\D/g, '');
    } else if (name === 'email') {
      processedValue = value.replace(/\s/g, '');
    }

    const setFunc = isEdit ? setEditForm : setForm;
    setFunc((prev) => ({ ...prev, [name]: processedValue }));
  };

  const handlePermissionChange = (module, permType, isEdit = false) => {
    const setFunc = isEdit ? setEditForm : setForm;
    setFunc((prev) => ({
      ...prev,
      permissions: { ...prev.permissions, [module]: { ...prev.permissions[module], [permType]: !prev.permissions[module][permType] } },
    }));
  };

  const validateForm = (formData, isEdit = false) => {
    if (!formData.name || !formData.name.trim()) {
      return 'Full Name is required';
    }
    if (/\d/.test(formData.name)) {
      return 'Name cannot contain numbers';
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailPattern.test(formData.email.trim())) {
      return 'Invalid email format';
    }

    const phone = (formData.phone || '').replace(/\s+/g, '');
    if (!/^[0-9]+$/.test(phone)) {
      return 'Phone number must contain only digits';
    }
    if (phone.length < 10 || phone.length > 15) {
      return 'Phone number must be between 10 and 15 digits';
    }

    if (!isEdit) {
      if (!formData.password || formData.password.length < 6) {
        return 'Password must be at least 6 characters';
      }
    }

    if (!formData.role) {
      return 'Role must be selected';
    }

    return '';
  };

  const resetAddAdminForm = () => {
    setForm({ name: '', email: '', phone: '', password: '', role: 'subadmin', permissions: makeDefaultPermissions() });
    setIsFormVisible(false);
    setExpandedModule(null);
    setMessage('');
  };

  const handleSubmit = async () => {
    const error = validateForm(form);
    if (error) {
      setMessage(`${error}`);
      setMsgType('error');
      setTimeout(() => setMessage(''), 5000);
      return;
    }
    setLoading(true);
    try {
      const userCred = await createUserWithEmailAndPassword(auth, form.email, form.password);
      const userData = {
        name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(),
        role: form.role, currentLevel: form.role, active: true,
        permissions: form.permissions || makeDefaultPermissions(), createdAt: new Date().toISOString(),
      };
      await set(ref(db, `HTAMS/users/${userCred.user.uid}`), userData);
      setMessage('Admin created successfully!');
      setMsgType('success');
      resetAddAdminForm();
      setTimeout(() => setMessage(''), 5000);
    } catch (error) {
      setMessage(`Error: ${error.message}`);
      setMsgType('error');
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async () => {
    if (!editForm) return;
    const error = validateForm(editForm, true);
    if (error) {
      setMessage(`${error}`);
      setMsgType('error');
      setTimeout(() => setMessage(''), 5000);
      return;
    }
    setLoading(true);
    try {
      await update(ref(db, `HTAMS/users/${selected}`), {
        name: editForm.name.trim(), email: editForm.email.trim(), phone: editForm.phone.trim(),
        role: editForm.role, currentLevel: editForm.role,
        permissions: editForm.permissions || makeDefaultPermissions(),
      });
      setMessage('User updated successfully!');
      setMsgType('success');
      setEditMode(false);
      setSelected(null);
      setTimeout(() => setMessage(''), 5000);
    } catch (error) {
      setMessage(`Error updating user: ${error.message}`);
      setMsgType('error');
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const toggleActivation = async (uid, currActive) => {
    try {
      await update(ref(db, `HTAMS/users/${uid}`), { active: !currActive });
      setMessage(`Admin has been ${!currActive ? 'activated' : 'deactivated'}.`);
      setMsgType('success');
      setTimeout(() => setMessage(''), 5000);
    } catch (error) {
      setMessage(`Error updating status: ${error.message}`);
      setMsgType('error');
      setTimeout(() => setMessage(''), 5000);
    }
  };

  const suspendAccount = async (uid) => {
    if (!customSuspendValue || customSuspendValue <= 0) {
      setMessage("Please enter a valid positive number for the duration.");
      setMsgType('error');
      setTimeout(() => setMessage(''), 5000);
      return;
    }
    const now = new Date();
    const value = parseInt(customSuspendValue, 10);
    switch (customSuspendUnit) {
      case 'hours': now.setHours(now.getHours() + value); break;
      case 'days': now.setDate(now.getDate() + value); break;
      case 'months': now.setMonth(now.getMonth() + value); break;
      case 'years': now.setFullYear(now.getFullYear() + value); break;
      default: break;
    }
    const until = now.toISOString();

    try {
      await update(ref(db, `HTAMS/users/${uid}`), { suspendedUntil: until });
      setMessage(`Account suspended for ${value} ${customSuspendUnit}.`);
      setMsgType('success');
      setSuspendMode(null);
      setTimeout(() => setMessage(''), 5000);
    } catch (error) {
      setMessage(`Error: ${error.message}`);
      setMsgType('error');
      setTimeout(() => setMessage(''), 5000);
    }
  };

  const renderDetailedPermissions = (permissions) => {
    if (!permissions) return <div className="no-permissions">No permissions set.</div>;
    const grantedModules = MODULES.filter(m => permissions[m] && Object.values(permissions[m]).some(v => v));
    if (grantedModules.length === 0) return <div className="no-permissions">No permissions granted.</div>;
    return (
      <div className="permissions-grid">
        {grantedModules.map((module) => (
          <div key={module} className="permission-module">
            <div className="permission-module-title">{module.charAt(0).toUpperCase() + module.slice(1)}</div>
            <div className="permission-tags">
              {['read', 'write', 'delete', 'add'].map((permType) => (
                permissions[module]?.[permType] && (
                  <span key={permType} className={`permission-tag ${permType}`}>
                    {permType}
                  </span>
                )
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const getCardUsers = (cardType) => {
    const adminsList = Object.entries(admins);
    switch (cardType) {
      case 'admin':
        return adminsList.filter(([, user]) => user.role === 'admin');
      case 'subadmin':
        return adminsList.filter(([, user]) => user.role === 'subadmin');
      case 'active':
        return adminsList.filter(([, user]) => user.active);
      case 'inactive':
        return adminsList.filter(([, user]) => !user.active);
      default:
        return [];
    }
  };

  const handleCardClick = (cardType) => {
    setSelectedCard(selectedCard === cardType ? null : cardType);
  };

  const filteredAdmins = useMemo(() => {
    return Object.entries(admins).filter(([uid, user]) => {
      const matchesSearch = (
        user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.phone?.includes(searchQuery)
      );

      if (!matchesSearch) return false;

      if (selectedCard === 'admin') return user.role === 'admin';
      if (selectedCard === 'subadmin') return user.role === 'subadmin';
      if (selectedCard === 'active') return user.active;
      if (selectedCard === 'inactive') return !user.active;

      return true;
    });
  }, [admins, searchQuery, selectedCard]);

  return (
    <div className="admin-page-container">
      {/* Header & Stats Compact Row */}
      <div className="header-stats-row">
        <div className="page-header-compact">
          <h1 className="page-title">
            <FiShield className="title-icon" />
            Admin Management
          </h1>
          <p className="page-subtitle">Manage system administrators</p>
        </div>

        <div className="stats-compact">
          <div
            className={`stat-pill ${selectedCard === 'admin' ? 'active' : ''} ${selectedCard === 'admin' ? 'admin' : ''}`}
            onClick={() => handleCardClick('admin')}
          >
            <span className="stat-label">Admins</span>
              <br></br>
            <span className="stat-value">{Object.values(admins).filter(u => u.role === 'admin').length}</span>
          </div>
          <div
            className={`stat-pill ${selectedCard === 'subadmin' ? 'active' : ''} ${selectedCard === 'subadmin' ? 'subadmin' : ''}`}
            onClick={() => handleCardClick('subadmin')}
          >
            <span className="stat-label">Sub-Admins</span>
            <br></br>
            <span className="stat-value">{Object.values(admins).filter(u => u.role === 'subadmin').length}</span>
          </div>
          <div
            className={`stat-pill ${selectedCard === 'active' ? 'active' : ''} ${selectedCard === 'active' ? 'active-stat' : ''}`}
            onClick={() => handleCardClick('active')}
          >
            <span className="stat-label">Active</span>
              <br></br>
            <span className="stat-value">{Object.values(admins).filter(u => u.active).length}</span>
          </div>
          <div
            className={`stat-pill ${selectedCard === 'inactive' ? 'active' : ''} ${selectedCard === 'inactive' ? 'inactive' : ''}`}
            onClick={() => handleCardClick('inactive')}
          >
            <span className="stat-label">Inactive</span>
              <br></br>
            <span className="stat-value">{Object.values(admins).filter(u => !u.active).length}</span>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="main-content-compact">
        {/* Toggleable Add Form */}
        {isFormVisible ? (
          <div className="add-admin-panel">
            <div className="panel-header">
              <h3><FiUserPlus /> Add New Admin</h3>
              <button className="close-panel-btn" onClick={resetAddAdminForm}><FiX /></button>
            </div>
            <div className="panel-body">
              <div className="compact-form-grid">
                <div className="form-group">
                  <label>Full Name</label>
                  <div className="input-wrapper">
                    <FiUser className="input-icon" />
                    <input
                      value={form.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      placeholder="Name"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <div className="input-wrapper">
                    <FiMail className="input-icon" />
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      placeholder="Email"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <div className="input-wrapper">
                    <FiPhone className="input-icon" />
                    <input
                      value={form.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      placeholder="Phone"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Password</label>
                  <div className="input-wrapper">
                    <FiLock className="input-icon" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={form.password}
                      onChange={(e) => handleChange('password', e.target.value)}
                      placeholder="******"
                    />
                    <button type="button" className="pass-toggle" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label>Role</label>
                  <div className="role-options">
                    <button
                      className={`role-choice ${form.role === 'admin' ? 'active' : ''}`}
                      onClick={() => handleChange('role', 'admin')}
                    >Admin</button>
                    <button
                      className={`role-choice ${form.role === 'subadmin' ? 'active' : ''}`}
                      onClick={() => handleChange('role', 'subadmin')}
                    >Sub-Admin</button>
                  </div>
                </div>
              </div>

              <div className="permissions-compact">
                <label>Permissions Module Access</label>
                <div className="modules-list">
                  {MODULES.map(mod => (
                    <div key={mod} className="module-item">
                      <div className="module-name">{mod.replace('-', ' ')}</div>
                      <div className="module-checks">
                        {['read', 'write', 'delete', 'add'].map(p => (
                          <div
                            key={p}
                            className={`perm-dot ${form.permissions[mod]?.[p] ? 'active' : ''}`}
                            onClick={() => handlePermissionChange(mod, p)}
                            title={p}
                          >
                            {p.charAt(0).toUpperCase()}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="panel-actions">
                <button className="btn-cancel" onClick={resetAddAdminForm}>Cancel</button>
                <button className="btn-save" onClick={handleSubmit} disabled={loading}>
                  {loading ? 'Creating...' : 'Create Admin'}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {/* Admins List Table Card */}
        <div className="table-card">
          <div className="table-header-row">
            <div className="table-title">
              <FiUsers /> Administrators List
              <span className="count-badge">{filteredAdmins.length}</span>
            </div>
            <div className="table-actions">
              <div className="search-compact">
                <FiSearch />
                <input
                  placeholder="Search admins..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              {!isFormVisible && (
                <button className="btn-add-compact" onClick={() => setIsFormVisible(true)}>
                  <FiPlus /> Add New
                </button>
              )}
            </div>
          </div>

          <div className="table-responsive">
            <table className="modern-table">
              <thead>
                <tr>
                  <th>User Details</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAdmins.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-8 text-gray-500">
                      No administrators found.
                    </td>
                  </tr>
                ) : (
                  filteredAdmins.map(([uid, user]) => {
                    const isSuspended = user.suspendedUntil && new Date(user.suspendedUntil) > new Date();
                    return (
                      <React.Fragment key={uid}>
                        <tr className={`admin-row ${!user.active ? 'opacity-75' : ''}`}>
                          <td>
                            <div className="user-cell">
                              <div className={`avatar-small ${user.role}`}>
                                {user.role === 'admin' ? <FiShield /> : <FiUser />}
                              </div>
                              <div className="user-info-text">
                                <div className="font-bold text-gray-800">{user.name}</div>
                                <div className="text-xs text-gray-500">{user.email}</div>
                                <div className="text-xs text-gray-400">{user.phone}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className={`badge-pill role-${user.role}`}>
                              {user.role}
                            </span>
                          </td>
                          <td>
                            <div className="status-cell">
                              <span className={`badge-dot ${user.active ? 'active' : 'inactive'}`}></span>
                              {user.active ? 'Active' : 'Inactive'}
                              {isSuspended && <span className="text-xs text-red-500 ml-2">(Suspended)</span>}
                            </div>
                          </td>
                          <td className="text-sm text-gray-500">
                            {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}
                          </td>
                          <td>
                            <div className="actions-cell">
                              <button
                                className="icon-btn text-blue-600"
                                onClick={() => {
                                  setEditForm({ ...user, password: '' });
                                  setEditMode(true);
                                  setSelected(uid);
                                }}
                                title="Edit"
                              >
                                <FiEdit3 />
                              </button>
                              <button
                                className={`icon-btn ${user.active ? 'text-red-500' : 'text-green-600'}`}
                                onClick={() => toggleActivation(uid, user.active)}
                                title={user.active ? 'Deactivate' : 'Activate'}
                              >
                                {user.active ? <FiEyeOff /> : <FiCheck />}
                              </button>
                              <button
                                className={`icon-btn ${selected === uid ? 'text-gray-800' : 'text-gray-400'}`}
                                onClick={() => setSelected(selected === uid ? null : uid)}
                                title="View Permissions"
                              >
                                <FiSettings />
                              </button>
                            </div>
                          </td>
                        </tr>
                        {selected === uid && (
                          <tr className="expanded-row">
                            <td colSpan="5">
                              <div className="expanded-details">
                                <div className="expanded-header">
                                  <h4>Permissions & Controls</h4>
                                  <div className="suspend-mini-form">
                                    <button
                                      className="text-xs text-orange-600 underline"
                                      onClick={() => setSuspendMode(suspendMode === uid ? null : uid)}
                                    >
                                      Suspend User
                                    </button>
                                    {suspendMode === uid && (
                                      <div className="suspend-popover">
                                        <input
                                          type="number"
                                          className="mini-input"
                                          value={customSuspendValue}
                                          onChange={(e) => setCustomSuspendValue(e.target.value)}
                                        />
                                        <select
                                          className="mini-select"
                                          value={customSuspendUnit}
                                          onChange={(e) => setCustomSuspendUnit(e.target.value)}
                                        >
                                          <option value="days">Days</option>
                                          <option value="hours">Hours</option>
                                        </select>
                                        <button onClick={() => suspendAccount(uid)} className="mini-btn-confirm">OK</button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="perms-summary-grid">
                                  {renderDetailedPermissions(user.permissions)}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Reused Edit Modal Logic */}
      {editMode && (
        <div className="modal-overlay" onClick={() => { setEditMode(false); setSelected(null); }}>
          <div className="modal compact-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FiEdit3 /> Edit Administrator</h3>
              <button className="close-btn" onClick={() => { setEditMode(false); setSelected(null); }}><FiX /></button>
            </div>
            <div className="modal-body">
              <div className="compact-form-grid">
                <div className="form-group">
                  <label>Full Name</label>
                  <input className="input-std" value={editForm?.name || ''} onChange={(e) => handleChange('name', e.target.value, true)} />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input className="input-std" value={editForm?.email || ''} onChange={(e) => handleChange('email', e.target.value, true)} />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input className="input-std" value={editForm?.phone || ''} onChange={(e) => handleChange('phone', e.target.value, true)} />
                </div>
                <div className="form-group">
                  <label>Role</label>
                  <select className="select-std" value={editForm?.role} onChange={(e) => handleChange('role', e.target.value, true)}>
                    <option value="admin">Admin</option>
                    <option value="subadmin">Sub-Admin</option>
                  </select>
                </div>
              </div>
              {/* Simplified Permissions for Edit Modal */}
              <div className="permissions-compact mt-4">
                <label>Update Permissions</label>
                <div className="modules-list" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {MODULES.map(mod => (
                    <div key={mod} className="module-item">
                      <div className="module-name">{mod}</div>
                      <div className="module-checks">
                        {['read', 'write', 'delete', 'add'].map(p => (
                          <div
                            key={p}
                            className={`perm-dot ${editForm?.permissions?.[mod]?.[p] ? 'active' : ''}`}
                            onClick={() => handlePermissionChange(mod, p, true)}
                            title={p}
                          >
                            {p[0].toUpperCase()}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => { setEditMode(false); setSelected(null); }}>Cancel</button>
              <button className="btn-save" onClick={handleEditSubmit} disabled={loading}>{loading ? 'Saving...' : 'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}

      {message && (
        <div className={`toast-compact ${msgType}`}>
          {msgType === 'success' ? <FiCheck /> : <FiX />}
          <span>{message}</span>
        </div>
      )}

      <style jsx>{`

      main-content-compact {
        padding: 1.5rem;
      }
        /* COMPACT PROFESSIONAL THEME */
        .admin-page-container {
      
          background: #f1f5f9;
          min-height: 100vh;
          font-family: 'Inter', sans-serif;
          color: #1e293b;
        }

        .header-stats-row {
          display: flex;
          background: var(--mobile-bg-primary);
          padding: 1.5rem;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .page-header-compact {
          min-width: 200px;
        }

        .page-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--mobile-text-primary);
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin: 0 0 0.25rem 0;
        }
        .title-icon { color: #2563eb; }
        .page-subtitle {
          color: var(--mobile-text-secondary);
          font-size: 0.875rem;
          margin: 0;
        }

        .stats-compact {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .stat-pill {
          background: var(--mobile-bg-primary-card);
          padding: 0.6rem 1rem;
          border-radius: 20px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05);
         color: var(--mobile-text-primary);
          align-items: center;
          cursor: pointer;
          border: 1px solid transparent;
          transition: all 0.2s;
        }
        .stat-pill:hover { border-color: #cbd5e1; transform: translateY(-1px); }
        .stat-pill.active { border-color: #2563eb; background: #eff6ff; }
        .stat-pill.active.admin { border-color: #2563eb; color: #1e40af; }
        .stat-pill.active.subadmin { border-color: #8b5cf6; color: #5b21b6; }
        .stat-pill.active.active-stat { border-color: #10b981; color: #065f46; }
        .stat-pill.active.inactive { border-color: #ef4444; color: #991b1b; }

        .stat-label { font-size: 0.75rem; font-weight: 600; color: inherit; text-transform: uppercase; }
        .stat-value { font-size: 1rem; font-weight: 800; color: inherit; }

        /* Add Panel */
        .add-admin-panel {
          background: white;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          margin-bottom: 1.5rem;
          box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
          overflow: hidden;
          animation: slideDown 0.3s ease;
        }
        @keyframes slideDown { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }

        .panel-header {
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #f1f5f9;
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #f8fafc;
        }
        .panel-header h3 { margin: 0; font-size: 1rem; font-weight: 600; display: flex; gap: 0.5rem; align-items: center; }
        .close-panel-btn { background: none; border: none; cursor: pointer; color: #94a3b8; font-size: 1.1rem; }
        .panel-body { padding: 1.5rem; }

        .compact-form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.25rem;
          margin-bottom: 1.5rem;
        }
        .form-group label { display: block; font-size: 0.75rem; font-weight: 600; color: #64748b; margin-bottom: 0.35rem; }
        .input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
        .input-icon { position: absolute; left: 0.75rem; color: #94a3b8; }
        .input-wrapper input, .input-std {
          width: 100%;
          padding: 0.625rem 0.75rem 0.625rem 2.25rem;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 0.875rem;
          outline: none;
          transition: border 0.2s;
        }
        .input-std { padding-left: 0.75rem; }
        .input-wrapper input:focus, .input-std:focus { border-color: #2563eb; ring: 2px solid rgba(37,99,235,0.1); }
        .pass-toggle { position: absolute; right: 0.75rem; background: none; border: none; cursor: pointer; color: #94a3b8; }

        .role-options { display: flex; gap: 0.5rem; }
        .role-choice {
          flex: 1;
          padding: 0.5rem;
          border: 1px solid #e2e8f0;
          background: white;
          border-radius: 6px;
          font-size: 0.8rem;
          cursor: pointer;
        }
        .role-choice.active { background: #eff6ff; color: #2563eb; border-color: #2563eb; font-weight: 600; }

        .permissions-compact { background: #f8fafc; padding: 1rem; border-radius: 8px; border: 1px solid #e2e8f0; }
        .permissions-compact label { font-size: 0.75rem; font-weight: 700; color: #475569; display: block; margin-bottom: 0.75rem; }
        .modules-list { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 0.75rem; }
        .module-item { background: white; padding: 0.5rem 0.75rem; border-radius: 6px; border: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
        .module-name { font-size: 0.75rem; font-weight: 600; text-transform: capitalize; color: #334155; }
        .module-checks { display: flex; gap: 0.25rem; }
        .perm-dot {
          width: 20px; height: 20px;
          border-radius: 4px;
          background: #f1f5f9;
          color: #cbd5e1;
          font-size: 0.6rem;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer;
          font-weight: 700;
        }
        .perm-dot.active { background: #10b981; color: white; }
        .perm-dot:hover { opacity: 0.8; }

        .panel-actions { display: flex; justify-content: flex-end; gap: 0.75rem; margin-top: 1.5rem; }
        .btn-cancel { padding: 0.5rem 1rem; border: 1px solid #e2e8f0; background: white; border-radius: 6px; cursor: pointer; font-size: 0.875rem; color: #64748b; }
        .btn-save { padding: 0.5rem 1.5rem; border: none; background: #2563eb; border-radius: 6px; cursor: pointer; font-size: 0.875rem; color: white; font-weight: 500; }
        .btn-save:hover { background: #1d4ed8; }

        /* Table Card */
        .table-card {
          background: white;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          overflow: hidden;
        }

        .table-header-row {
          padding: 1rem 1.5rem;
          background: var(--mobile-bg-primary-card);
          border-bottom: 1px solid #f1f5f9;
          display: flex;
          color: var(--mobile-text-primary);
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
        }
        .table-title { font-size: 1rem; font-weight: 600; color: var(--mobile-text-primary); display: flex; align-items: center; gap: 0.5rem; }
        .count-badge { background: #f1f5f9; color: #64748b; font-size: 0.75rem; padding: 2px 8px; border-radius: 12px; }
        .table-actions { display: flex; gap: 0.75rem; align-items: center; }
        .search-compact {
          position: relative;
          width: 200px;
        }
        .search-compact svg { position: absolute; left: 0.75rem; top: 50%; transform: translateY(-50%); color: #94a3b8; }
        .search-compact input {
          width: 100%;
          padding: 0.5rem 0.5rem 0.5rem 2rem;
          font-size: 0.875rem;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          outline: none;
        }
        .btn-add-compact {
          background: #2563eb;
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 6px;
          font-size: 0.8rem;
          cursor: pointer;
          display: flex; align-items: center; gap: 0.4rem;
          white-space: nowrap;
        }

        /* Modern Table */
        .modern-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.875rem;
        }
        .modern-table th {
          text-align: left;
          padding: 0.875rem 1.5rem;
          background: var(--mobile-bg-primary);
          color: var(--mobile-text-primary);
          font-weight: 600;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .modern-table td {
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #f1f5f9;
          vertical-align: middle;
        }
        .modern-table tr:last-child td { border-bottom: none; }
        .text-right { text-align: right; }
        .text-center { text-align: center; }

        .user-cell { display: flex; align-items: center; gap: 0.75rem; }
        .avatar-small {
          width: 36px; height: 36px; border-radius: 8px;
          background: #eff6ff; color: #2563eb;
          display: flex; align-items: center; justify-content: center;
        }
        .avatar-small.admin { background: #eff6ff; color: #2563eb; }
        .avatar-small.subadmin { background: #f5f3ff; color: #7c3aed; }
        
        .user-info-text { display: flex; flex-direction: column; }
        .user-info-text .font-bold { color: #0f172a; font-weight: 600; }

        .badge-pill { display: inline-block; padding: 2px 10px; border-radius: 99px; font-size: 0.7rem; font-weight: 600; text-transform: uppercase; }
        .badge-pill.role-admin { background: #eff6ff; color: #1e40af; border: 1px solid #dbeafe; }
        .badge-pill.role-subadmin { background: #f5f3ff; color: #5b21b6; border: 1px solid #ddd6fe; }

        .status-cell { display: flex; align-items: center; gap: 0.4rem; font-weight: 500; color: #334155; }
        .badge-dot { width: 8px; height: 8px; border-radius: 50%; }
        .badge-dot.active { background: #10b981; }
        .badge-dot.inactive { background: #ef4444; }

        .actions-cell { display: flex; justify-content: flex-end; gap: 0.5rem; opacity: 0.8; }
        .icon-btn {
          width: 32px; height: 32px;
          border-radius: 6px;
          border: 1px solid transparent;
          background: transparent;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        }
        .icon-btn:hover { background: #f1f5f9; border-color: #cbd5e1; }
        .text-blue-600 { color: #2563eb; }
        .text-red-500 { color: #ef4444; }
        .text-green-600 { color: #16a34a; }

        /* Expanded Row */
        .expanded-row td { background: #f8fafc; padding: 0; box-shadow: inset 0 2px 4px rgba(0,0,0,0.02); }
        .expanded-details { padding: 1.5rem; }
        .expanded-header { display: flex; justify-content: space-between; margin-bottom: 1rem; border-bottom: 1px solid #e2e8f0; padding-bottom: 0.5rem; }
        .expanded-header h4 { font-size: 0.875rem; color: #64748b; font-weight: 700; text-transform: uppercase; margin: 0; }
        
        .suspend-mini-form { position: relative; display: flex; gap: 0.5rem; align-items: center; }
        .suspend-popover {
          position: absolute; right: 0; top: 100%; mt-2;
          background: white; border: 1px solid #e2e8f0; padding: 10px;
          border-radius: 8px; box-shadow: 0 4px 10px rgba(0,0,0,0.1);
          z-index: 10; display: flex; gap: 5px;
        }
        .mini-input, .mini-select { padding: 4px; border: 1px solid #e2e8f0; border-radius: 4px; font-size: 12px; }
        .mini-btn-confirm { padding: 4px 8px; background: #ef4444; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 12px; }

        .perms-summary-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 0.75rem; }

        /* Modals */
        .compact-modal { width: 500px; max-width: 95vw; }
        .modal-body { padding: 1.5rem; }
        .modal-footer { padding: 1rem 1.5rem; background: #f8fafc; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 0.75rem; }
        .select-std { width: 100%; padding: 0.625rem; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 0.875rem; outline: none; }

        .toast-compact {
          position: fixed; bottom: 2rem; right: 2rem;
          padding: 0.75rem 1.25rem; border-radius: 8px;
          color: white; font-weight: 500; font-size: 0.875rem;
          display: flex; align-items: center; gap: 0.75rem;
          box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
          z-index: 9999;
          animation: slideUp 0.3s ease;
        }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .toast-compact.success { background: #10b981; }
        .toast-compact.error { background: #ef4444; }

        /* Responsive */
        @media (max-width: 768px) {
          .admin-page-container { padding: 0.5rem; }
          .header-stats-row { flex-direction: column; align-items: stretch; gap: 1rem; margin-bottom: 1rem; }
          .stats-compact { overflow-x: auto; padding-bottom: 5px; }
          .stat-pill { white-space: nowrap; }
          .modern-table { display: block; overflow-x: auto; white-space: nowrap; }
          .panel-header, .table-header-row { padding: 0.75rem 1rem; }
          .panel-body, .table-responsive { padding: 0; }
          .compact-form-grid { grid-template-columns: 1fr; }
          .search-compact { width: 100%; order: 2; margin-top: 0.5rem; }
          .table-actions { flex-direction: column; align-items: flex-end; width: 100%; }
          .btn-add-compact { width: 100%; justify-content: center; }
        }
      `}</style>
    </div>
  );
};

export default AddAdminPage;
